import { useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useAppStore } from '../store/useAppStore.js';
import {
  updateDisplayName,
  verifyCurrentPassword,
  updatePassword,
  getMyGuideProfile,
  updateGuideProfile,
  sendFeedback,
  getPaymentMethod,
  createSetupIntent,
  deletePaymentMethod,
} from '../api/client.js';
import { CardOnFileModal } from './CardOnFileModal.js';
import { PasswordField } from '../PasswordField.js';
import { SMS_CONSENT_TEXT, type SavedCard } from '@aslgo/shared';
import { ConsentText } from '../components/ConsentText.js';
import { AlertIcon, CheckIcon } from '../icons.js';
import { notificationsSupported, getPermission, requestPermission, showTest } from '../lib/notifications.js';
import { InfoModal } from '../registration/InfoModal.js';
import { CheckboxGroup } from '../registration/inputs.js';
import { AVAILABILITY_SLOTS } from '../registration/types.js';

const SUPPORT_URL = 'https://aslgo.com/contact-us';

type Status = { kind: 'success' | 'error'; msg: string } | null;

function errMsg(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong. Please try again.';
}

export function ProfileView() {
  const role = useAppStore((s) => s.role);
  const isGuide = role === 'guide';
  const isLearner = role === 'learner';

  return (
    <div className="animate-fade-up">
      <div className="mb-8">
        <h1 className="h-display text-ink-900 dark:text-ink-100">Your profile</h1>
        <p className="hint mt-1">Manage your account, preferences, and settings.</p>
      </div>

      <div className="flex flex-col gap-5">
        <DisplayNameSection />
        {isGuide ? <GuideSmsSection /> : null}
        {isGuide ? <DesktopNotificationsSection /> : null}
        <PasswordSection />
        {isGuide ? <AvailabilitySection /> : null}
        {isGuide ? <GuidePaymentSection /> : null}
        <BillingSection isLearner={isLearner} />
        <FeedbackSection />
        <SupportSection />
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Sections
// -------------------------------------------------------------------------

function DisplayNameSection() {
  const displayName = useAppStore((s) => s.displayName);
  const refreshProfile = useAppStore((s) => s.refreshProfile);
  const [name, setName] = useState(displayName ?? '');
  const [status, setStatus] = useState<Status>(null);
  const [saving, setSaving] = useState(false);

  async function onSave(e: FormEvent) {
    e.preventDefault();
    setStatus(null);
    setSaving(true);
    try {
      await updateDisplayName(name);
      await refreshProfile();
      setStatus({ kind: 'success', msg: 'Display name updated.' });
    } catch (err) {
      setStatus({ kind: 'error', msg: errMsg(err) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Section title="Display Name" description="The name other people see in the lobby and on calls.">
      <form onSubmit={onSave}>
        <Field label="Display name">
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="nickname"
            required
          />
        </Field>
        <SaveButton disabled={saving}>{saving ? 'Saving…' : 'Save'}</SaveButton>
        <Notice status={status} />
      </form>
    </Section>
  );
}

function GuideSmsSection() {
  const [phone, setPhone] = useState('');
  const [optIn, setOptIn] = useState(false);
  const [status, setStatus] = useState<Status>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    getMyGuideProfile()
      .then((g) => {
        if (!active || !g) return;
        setPhone(g.phone ?? '');
        setOptIn(g.smsOptIn);
      })
      .catch(() => { /* leave defaults; user can still set them */ });
    return () => { active = false; };
  }, []);

  async function onSave(e: FormEvent) {
    e.preventDefault();
    setStatus(null);
    setSaving(true);
    try {
      const saved = await updateGuideProfile({ phone, smsOptIn: optIn });
      setOptIn(saved.smsOptIn);
      setPhone(saved.phone ?? '');
      setStatus({
        kind: 'success',
        msg: saved.smsOptIn
          ? 'You are now opted in to ASL Go text notifications.'
          : 'You are now opted out of ASL Go text notifications.',
      });
    } catch (err) {
      setStatus({ kind: 'error', msg: errMsg(err) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Section title="Text notifications" description="Get session requests, reminders, and updates by SMS.">
      <form onSubmit={onSave}>
        <Field label="Phone number">
          <input
            className="input"
            type="tel"
            inputMode="tel"
            placeholder="(555) 555-5555"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            autoComplete="tel"
          />
        </Field>

        <label className="mt-5 flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 shrink-0 accent-brand"
            checked={optIn}
            onChange={(e) => setOptIn(e.target.checked)}
          />
          <span className="text-sm font-medium text-ink-800 dark:text-ink-100">
            I want to receive ASL Go text notifications.
          </span>
        </label>

        <p className="mt-3 text-[0.8rem] leading-relaxed text-ink-500 dark:text-ink-400">
          <ConsentText text={SMS_CONSENT_TEXT} />
        </p>

        <SaveButton disabled={saving}>{saving ? 'Saving…' : 'Save Preferences'}</SaveButton>
        <Notice status={status} />
      </form>
    </Section>
  );
}

/** Guide-only. Lets a guide confirm desktop notifications actually reach them,
 *  and explains how to fix it when they don't. Exported so tests can mount it
 *  without the whole profile page. */
export function DesktopNotificationsSection() {
  const [status, setStatus] = useState<Status>(null);
  const [showHelp, setShowHelp] = useState(false);

  const BLOCKED = 'Notifications are blocked in your browser. Open "Set it up" for how to turn them on.';

  async function onTest() {
    setStatus(null);
    if (!notificationsSupported()) {
      setStatus({ kind: 'error', msg: "This browser can't show desktop notifications. Try Chrome on a laptop or desktop." });
      return;
    }
    let permission = getPermission();
    if (permission === 'default') permission = await requestPermission();
    if (permission !== 'granted') {
      setStatus({ kind: 'error', msg: BLOCKED });
      return;
    }
    setStatus(showTest()
      ? { kind: 'success', msg: 'Sent — check the top-right of your screen.' }
      : { kind: 'error', msg: 'Your browser wouldn\'t show the notification. Open "Set it up" for how to turn them on.' });
  }

  return (
    <Section title="Desktop notifications" description="Get a popup when a learner calls, even when this tab is in the background.">
      <div className="flex items-center gap-4">
        <button type="button" className="btn-primary" onClick={() => { void onTest(); }}>
          Send a test notification
        </button>
        <button
          type="button"
          className="text-[0.85rem] underline text-ink-600 dark:text-ink-400"
          onClick={() => setShowHelp(true)}
        >
          Set it up
        </button>
      </div>
      <Notice status={status} />
      {showHelp ? <NotificationSetupModal onClose={() => setShowHelp(false)} /> : null}
    </Section>
  );
}

function NotificationSetupModal({ onClose }: { onClose: () => void }) {
  return (
    <InfoModal title="Turn on desktop notifications" onClose={onClose}>
      <h3 className="font-semibold text-ink-900 dark:text-ink-100">Chrome (macOS)</h3>
      <ol className="list-decimal pl-5 space-y-1">
        <li>When the browser asks, click <strong>Allow</strong>.</li>
        <li>If you blocked it before: click the icon at the left of the address bar, then <strong>Site settings</strong> → <strong>Notifications</strong> → <strong>Allow</strong>.</li>
        <li>Open <strong>System Settings → Notifications → Google Chrome</strong>. Turn on <strong>Allow Notifications</strong> and set the style to <strong>Alerts</strong> so the popup stays on screen.</li>
        <li>Turn off <strong>Do Not Disturb / Focus</strong>.</li>
      </ol>
      <h3 className="font-semibold text-ink-900 dark:text-ink-100">Safari (macOS)</h3>
      <ol className="list-decimal pl-5 space-y-1">
        <li>Open <strong>Safari → Settings → Websites → Notifications</strong>, find ASL Go and set it to <strong>Allow</strong>.</li>
        <li>Open <strong>System Settings → Notifications → Safari</strong> and turn on <strong>Allow Notifications</strong>.</li>
        <li>Turn off <strong>Do Not Disturb / Focus</strong>.</li>
      </ol>
    </InfoModal>
  );
}

function PasswordSection() {
  const email = useAppStore((s) => s.email);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState<Status>(null);
  const [saving, setSaving] = useState(false);

  async function onSave(e: FormEvent) {
    e.preventDefault();
    setStatus(null);
    if (next.length < 6) {
      setStatus({ kind: 'error', msg: 'New password must be at least 6 characters.' });
      return;
    }
    if (next !== confirm) {
      setStatus({ kind: 'error', msg: 'New passwords do not match.' });
      return;
    }
    if (!email) {
      setStatus({ kind: 'error', msg: 'Could not determine your account email. Please sign in again.' });
      return;
    }
    setSaving(true);
    try {
      const ok = await verifyCurrentPassword(email, current);
      if (!ok) {
        setStatus({ kind: 'error', msg: 'Current password is incorrect.' });
        return;
      }
      await updatePassword(next);
      setCurrent('');
      setNext('');
      setConfirm('');
      setStatus({ kind: 'success', msg: 'Password updated.' });
    } catch (err) {
      setStatus({ kind: 'error', msg: errMsg(err) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Section title="Password" description="Change the password you use to sign in.">
      <form onSubmit={onSave} className="flex flex-col gap-4">
        <PasswordField label="Current password" value={current} onChange={setCurrent}
          autoComplete="current-password" required />
        <PasswordField label="New password" value={next} onChange={setNext}
          autoComplete="new-password" minLength={6} required />
        <PasswordField label="Confirm new password" value={confirm} onChange={setConfirm}
          autoComplete="new-password" required />
        <div>
          <SaveButton disabled={saving}>{saving ? 'Updating…' : 'Update password'}</SaveButton>
        </div>
        <Notice status={status} />
      </form>
    </Section>
  );
}

function AvailabilitySection() {
  const [slots, setSlots] = useState<string[]>([]);
  const [status, setStatus] = useState<Status>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    getMyGuideProfile()
      .then((g) => {
        if (active && g) {
          // Drop any stored id that isn't currently rendered (e.g. a slot
          // renamed or removed) so it can't be silently re-submitted and
          // 400 forever without a way to clear it from the UI.
          setSlots(g.availabilitySlots.filter((id) => AVAILABILITY_SLOTS.some((s) => s.id === id)));
        }
      })
      .catch(() => { /* leave empty; the guide can still pick */ });
    return () => { active = false; };
  }, []);

  const toggle = (id: string) =>
    setSlots((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  async function onSave(e: FormEvent) {
    e.preventDefault();
    setStatus(null);
    setSaving(true);
    try {
      // No minimum here, unlike registration: clearing every box is a valid
      // answer — the guide has no usual times.
      const saved = await updateGuideProfile({ availabilitySlots: slots });
      setSlots(saved.availabilitySlots);
      setStatus({ kind: 'success', msg: 'Availability updated.' });
    } catch (err) {
      setStatus({ kind: 'error', msg: errMsg(err) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Section title="When you're available" description="Let learners know the times you can usually take calls.">
      <form onSubmit={onSave}>
        <CheckboxGroup legend="Preferred availability" options={AVAILABILITY_SLOTS} values={slots} onToggle={toggle} />
        <SaveButton disabled={saving}>{saving ? 'Saving…' : 'Save'}</SaveButton>
        <Notice status={status} />
      </form>
    </Section>
  );
}

/** Guides are paid through Rippling, so there is nothing to collect here — just
 *  a pointer. */
function GuidePaymentSection() {
  return (
    <Section title="Payment information" description="Where your payout and tax details live.">
      <p className="text-sm text-ink-700 dark:text-ink-300">
        Your payment information is stored at{' '}
        <a
          href="https://www.rippling.com"
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-ink-900 dark:text-ink-100"
        >
          Rippling.com
        </a>.
      </p>
    </Section>
  );
}

function BillingSection({ isLearner }: { isLearner: boolean }) {
  if (!isLearner) return null;
  return (
    <Section title="Billing" description="Payments and payouts.">
      <LearnerBilling />
    </Section>
  );
}

/** Learner card-on-file management: shows the saved card (brand/last4/exp — never
 *  the PAN, which only ever lives in Stripe's iframe) and lets the learner
 *  add/update it via a SetupIntent-backed CardOnFileModal, or remove it.
 *  Exported standalone so it can be mounted directly in tests. */
export function LearnerBilling() {
  const [card, setCard] = useState<SavedCard | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    getPaymentMethod()
      .then((res) => { if (active) setCard(res.card); })
      .catch((err) => { if (active) setStatus({ kind: 'error', msg: errMsg(err) }); });
    return () => { active = false; };
  }, []);

  async function openModal() {
    setStatus(null);
    try {
      const { clientSecret: secret } = await createSetupIntent();
      setClientSecret(secret);
    } catch (err) {
      setStatus({ kind: 'error', msg: errMsg(err) });
    }
  }

  function onSaved(saved: SavedCard | null) {
    setCard(saved);
    setClientSecret(null);
    setStatus({ kind: 'success', msg: 'Card saved.' });
  }

  async function remove() {
    setBusy(true);
    setStatus(null);
    try {
      const res = await deletePaymentMethod();
      setCard(res.card);
      setStatus({ kind: 'success', msg: 'Card removed.' });
    } catch (err) {
      setStatus({ kind: 'error', msg: errMsg(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      {card ? (
        <div className="flex items-center justify-between">
          <span className="text-ink-800 dark:text-ink-200 capitalize">
            {card.brand} ··{card.last4} · exp {card.expMonth}/{card.expYear}
          </span>
          <span className="flex gap-2">
            <button type="button" className="btn-ghost" onClick={() => { void openModal(); }} disabled={busy}>Update</button>
            <button type="button" className="btn-ghost text-status-error" onClick={() => { void remove(); }} disabled={busy}>Remove</button>
          </span>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <span className="hint">No card saved.</span>
          <button type="button" className="btn-primary" onClick={() => { void openModal(); }}>Add card</button>
        </div>
      )}
      <Notice status={status} />
      {clientSecret ? (
        <CardOnFileModal clientSecret={clientSecret} onSaved={onSaved} onClose={() => setClientSecret(null)} />
      ) : null}
    </div>
  );
}

function FeedbackSection() {
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<Status>(null);
  const [saving, setSaving] = useState(false);

  async function onSend(e: FormEvent) {
    e.preventDefault();
    setStatus(null);
    if (!message.trim()) {
      setStatus({ kind: 'error', msg: 'Please enter a message.' });
      return;
    }
    setSaving(true);
    try {
      await sendFeedback(message);
      setMessage('');
      setStatus({ kind: 'success', msg: 'Thanks — your feedback was sent.' });
    } catch (err) {
      setStatus({ kind: 'error', msg: errMsg(err) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Section title="Got feedback?" description="Tell us what's working and what isn't. It goes straight to the team.">
      <form onSubmit={onSend}>
        <Field label="Your feedback">
          <textarea
            className="input min-h-[110px] resize-y"
            placeholder="Share an idea, a bug, or anything on your mind…"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </Field>
        <SaveButton disabled={saving}>{saving ? 'Sending…' : 'Send feedback'}</SaveButton>
        <Notice status={status} />
      </form>
    </Section>
  );
}

function SupportSection() {
  return (
    <Section title="Customer support" description="Need a hand? Our team is here to help.">
      <a
        href={SUPPORT_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center rounded-lg bg-ink-900 text-white dark:bg-white dark:text-ink-900 px-4 py-2 text-sm font-semibold hover:opacity-90 transition"
      >
        Contact support →
      </a>
    </Section>
  );
}

// -------------------------------------------------------------------------
// Shared building blocks
// -------------------------------------------------------------------------

function Section({ title, description, children }: { title: string; description?: string; children: ReactNode }) {
  return (
    <section className="card p-6 sm:p-7">
      <h2 className="h-card text-ink-900 dark:text-ink-100">{title}</h2>
      {description ? <p className="hint mt-1 mb-5">{description}</p> : <div className="mb-5" />}
      {children}
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}

function SaveButton({ children, disabled }: { children: ReactNode; disabled?: boolean }) {
  return (
    <button
      type="submit"
      disabled={disabled}
      className="mt-5 inline-flex items-center justify-center rounded-lg bg-ink-900 text-white
                 dark:bg-white dark:text-ink-900 px-4 py-2 text-sm font-semibold
                 hover:opacity-90 disabled:opacity-50 transition"
    >
      {children}
    </button>
  );
}

function Notice({ status }: { status: Status }) {
  if (!status) return null;
  if (status.kind === 'error') {
    return (
      <div className="notice-error mt-4">
        <AlertIcon className="shrink-0 mt-0.5" />
        <span>{status.msg}</span>
      </div>
    );
  }
  return (
    <div
      role="status"
      className="mt-4 flex items-start gap-2 rounded-lg bg-status-success/10 px-3 py-2 text-sm
                 text-status-success dark:text-status-successBright ring-1 ring-status-success/20"
    >
      <CheckIcon className="shrink-0 mt-0.5" />
      <span>{status.msg}</span>
    </div>
  );
}

